import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SVG from "react-inlinesvg";
import { Tooltip } from "react-tooltip";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconAdd from "../../assets/svg/add.svg";

import bannerStyles from "../../styles/composable/banner.module.scss";

type IProps = Immutable<{
    index: number;
    left: number | null;
    right: number | null;
    top: number | null;
    bottom: number | null;
    title: string;
    description: string;
}>;

export const ProductAdvertTooltip = (props: IProps): React.JSX.Element => {
    const tipID = `tooltip-banner__tooltip-control-${props.index}`;
    const spanRef = useRef<HTMLSpanElement>(null);
    const portalTargetRef = useRef<HTMLElement | null>(null);
    const [mounted, setMounted] = useState(false);
    const [placement, setPlacement] = useState<"top" | "left" | "right">("top");
    const [width, setWidth] = useState(300);

    useEffect(() => {
        const document = getDocument();
        const elem = spanRef.current;
        if (!document || !elem) return;
        const rect = elem.getBoundingClientRect();
        const offsetLeft = rect.left + document.body.scrollLeft;
        const bodyWidth = document.body.offsetWidth;
        const offsetRight = bodyWidth - offsetLeft;
        let nextPlacement: "right" | "left" | "top";
        let nextWidth: number;
        if (offsetLeft < bodyWidth / 3) {
            nextPlacement = "right";
            nextWidth = Math.min(300, bodyWidth - offsetLeft - 20);
        } else if (offsetRight < bodyWidth / 3) {
            nextPlacement = "left";
            nextWidth = Math.min(300, offsetRight - 20);
        } else {
            nextPlacement = "top";
            nextWidth = Math.min(300, bodyWidth - 20);
        }
        setPlacement(nextPlacement);
        setWidth(nextWidth);
        portalTargetRef.current = document.body;
        setMounted(true);
    }, []);

    return (
        <span
            ref={spanRef}
            id={`banner__tooltip-control-${props.index}`}
            className={bannerStyles.bannerTooltipControl}
            style={{
                left: props.left != null ? `${props.left}%` : undefined,
                right: props.right != null ? `${props.right}%` : undefined,
                top: props.top != null ? `${props.top}%` : undefined,
                bottom: props.bottom != null ? `${props.bottom}%` : undefined,
            }}
        >
            <span
                data-tooltip-id={tipID}
                className={bannerStyles.bannerTooltipIconContainer}
            >
                <SVG
                    title={t`Plus Icon`}
                    className={bannerStyles.bannerTooltipIcon}
                    src={iconAdd}
                />
            </span>
            {mounted &&
                portalTargetRef.current &&
                createPortal(
                    <Tooltip
                        id={tipID}
                        variant="light"
                        clickable={true}
                        float={true}
                        globalCloseEvents={{ escape: true }}
                        place={placement}
                    >
                        <div style={{ width: width }}>
                            <h2
                                dangerouslySetInnerHTML={{
                                    __html: props.title,
                                }}
                            ></h2>
                            <p>{props.description}</p>
                        </div>
                    </Tooltip>,
                    portalTargetRef.current,
                )}
        </span>
    );
};
